<script setup lang="ts">
const props = defineProps<{
  username: string
}>()

const emit = defineEmits<{
  close: []
}>()

const toast = useToast()

const profileUrl = computed(() => `https://mychar.gg/${props.username}`)
const qrUrl = computed(() => `${profileUrl.value}?ref=qr`)
const displayUrl = computed(() => `mychar.gg/${props.username}`)

const copied = ref(false)
const canShare = ref(false)
const qrContainer = ref<HTMLElement | null>(null)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let qrCode: any = null

onMounted(async () => {
  canShare.value = !!navigator.share

  const { default: QRCodeStyling } = await import('qr-code-styling')
  qrCode = new QRCodeStyling({
    width: 280,
    height: 280,
    data: qrUrl.value,
    image: '/logo_short_transparent.png',
    dotsOptions: {
      type: 'rounded',
      gradient: {
        type: 'linear',
        rotation: 2.35,
        colorStops: [
          { offset: 0, color: '#1A1265' },
          { offset: 1, color: '#EBEBDF' }
        ]
      }
    },
    cornersSquareOptions: {
      type: 'extra-rounded',
      color: '#1A1265'
    },
    cornersDotOptions: {
      type: 'dot',
      color: '#1A1265'
    },
    backgroundOptions: {
      color: '#EBEBDF'
    },
    imageOptions: {
      crossOrigin: 'anonymous',
      margin: 8
    },
    qrOptions: {
      errorCorrectionLevel: 'H'
    }
  })

  if (qrContainer.value) {
    qrCode.append(qrContainer.value)
  }
})

async function copyUrl() {
  try {
    await navigator.clipboard.writeText(profileUrl.value)
    copied.value = true
    toast.add({ title: 'Link copied to clipboard', color: 'success' })
    setTimeout(() => { copied.value = false }, 2000)
  } catch {
    toast.add({ title: 'Failed to copy link', color: 'error' })
  }
}

async function downloadQr() {
  if (!qrCode) return
  qrCode.download({ name: `mychar-${props.username}`, extension: 'png' })
}

async function nativeShare() {
  try {
    await navigator.share({
      title: `@${props.username} on mychar.gg`,
      text: `Check out @${props.username}'s character page on mychar.gg`,
      url: profileUrl.value
    })
  } catch {
    // User cancelled or share failed silently
  }
}
</script>

<template>
  <UModal>
    <template #header="{ close }">
      <div class="flex items-center justify-between w-full gap-3">
        <div class="flex items-center gap-3">
          <div class="flex items-center justify-center size-10 rounded-full bg-primary-500/15 border border-primary-400/25">
            <UIcon
              name="i-lucide-share-2"
              class="size-5 text-primary-400"
            />
          </div>
          <h3 class="text-base font-semibold text-highlighted">
            Share Character
          </h3>
        </div>
        <UTooltip text="Close">
          <UButton
            color="neutral"
            variant="ghost"
            icon="i-lucide-x"
            @click="close(); emit('close')"
          />
        </UTooltip>
      </div>
    </template>

    <template #body>
      <div class="space-y-5">
        <!-- URL with copy -->
        <div class="flex items-center gap-2">
          <div class="flex-1 min-w-0 px-3 py-2 rounded-lg bg-elevated/50 border border-default">
            <span class="text-sm text-toned font-mono truncate block">
              {{ displayUrl }}
            </span>
          </div>
          <UButton
            :icon="copied ? 'i-lucide-check' : 'i-lucide-copy'"
            :color="copied ? 'success' : 'neutral'"
            variant="soft"
            @click="copyUrl"
          />
        </div>

        <!-- QR Code -->
        <div class="flex justify-center">
          <div
            ref="qrContainer"
            class="rounded-xl overflow-hidden"
          />
        </div>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center gap-3 w-full">
        <UButton
          label="Download QR"
          icon="i-lucide-download"
          color="neutral"
          variant="soft"
          @click="downloadQr"
        />

        <div class="flex-1" />

        <UButton
          v-if="canShare"
          label="Share"
          icon="i-lucide-share"
          color="primary"
          @click="nativeShare"
        />
      </div>
    </template>
  </UModal>
</template>
